import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, UserCheck, Leaf, BookOpen, RotateCcw, Sprout } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loader, Card, Disclaimer } from "@/components/Common";
import PhotoStep from "@/components/analysis/PhotoStep";
import IdentificationStep from "@/components/analysis/IdentificationStep";
import AnamnesisStep from "@/components/analysis/AnamnesisStep";
import ContextStep from "@/components/analysis/ContextStep";
import ResultCard from "@/components/analysis/ResultCard";
import ResultSummary, { pnPending } from "@/components/analysis/ResultSummary";
import SpeciesConfirm from "@/components/analysis/SpeciesConfirm";
import ReassessPanel from "@/components/analysis/ReassessPanel";
import ShareButton from "@/components/ShareButton";
import SavePlantDialog from "@/components/analysis/SavePlantDialog";

const STEP_TEXTS = {
  analyze: ["Osservo le foto…", "Cerco i segni del problema…", "Confronto le possibili cause…", "Preparo le indicazioni…"],
  analyze_final: ["Aggiorno l'analisi con le tue risposte…", "Confronto le possibili cause…", "Preparo le indicazioni…"],
  species_fix: ["Aggiorno l'analisi con la specie corretta…", "Confronto le possibili cause…", "Preparo le indicazioni…"],
  reassess: ["Esamino le nuove foto insieme a quelle iniziali…", "Rivaluto la diagnosi con le informazioni aggiunte…", "Preparo le indicazioni…"],
  identify: ["Osservo le foto e i caratteri della pianta…", "Confronto i risultati di due sistemi di riconoscimento…", "Verifico l'attendibilità dell'identificazione…",
    "Sto completando l'identificazione. Un po' di pazienza: sto verificando con precisione il risultato."],
  assess: ["Esamino le foto insieme alle informazioni che mi hai dato…", "Individuo le possibili cause compatibili con i sintomi…", "Valuto se servono altre domande o altre foto…",
    "Sto completando la valutazione. Un po' di pazienza: sto preparando una risposta dettagliata."],
  diagnose: ["Analizzo i sintomi e le informazioni fornite…", "Confronto le possibili cause…", "Preparo le indicazioni di cura…",
    "Sto completando l'analisi. Un po' di pazienza: sto definendo le indicazioni più adatte per intervenire."],
};
const WAIT_NOTE_V3 = "Di solito servono meno di 30 secondi.";
const WAIT_NOTE = "Stiamo preparando un'analisi specialistica della tua pianta. Le immagini e le informazioni fornite vengono esaminate e confrontate per offrirti una risposta accurata e personalizzata.";

function Working({ step }) {
  const texts = STEP_TEXTS[step] || ["Analisi in corso…"];
  const [i, setI] = useState(0);
  useEffect(() => {
    setI(0);
    const t = setInterval(() => setI((n) => Math.min(n + 1, texts.length - 1)), 4000);
    return () => clearInterval(t);
  }, [step, texts.length]);
  const text = texts[Math.min(i, texts.length - 1)];
  return (
    <div className="flex flex-col items-center px-8 py-24 text-center" data-testid="analysis-working">
      <span className="relative flex h-20 w-20 items-center justify-center rounded-full border border-[#C9A96E]">
        <Sprout className="text-[#0D2517]" size={30} />
        <Loader2 className="absolute -right-1 -top-1 animate-spin text-[#C9A96E]" size={22} />
      </span>
      <p className="mt-6 max-w-sm text-[17px] leading-relaxed text-[#5b6b60]" data-testid="analysis-working-note">{step?.startsWith("analyze") || step === "species_fix" || step === "reassess" ? WAIT_NOTE_V3 : WAIT_NOTE}</p>
      <p key={text} className="mt-6 max-w-sm font-serif text-2xl text-[#0D2517] animate-in fade-in duration-700" data-testid="analysis-working-message">{text}</p>
    </div>
  );
}

const isLemon = (sp) => /citrus\s*(×\s*|x\s+)?limon\b/i.test(sp?.botanical_name || "") || /^limon[ei]\b/i.test(sp?.common_name || "");

function GuideCta({ config, reason }) {
  return (
    <Card testid="guide-cta" accent>
      <div className="flex gap-3">
        <BookOpen className="mt-1 shrink-0 text-[#B39257]" size={20} />
        <div>
          <p className="eyebrow">Guida digitale</p>
          <p className="font-serif text-lg font-semibold leading-tight text-[#0D2517]">{config.guide_title}</p>
          <p className="text-xs text-[#5b6b60]">di {config.guide_author}</p>
          {reason && <p className="mt-2 text-xs text-[#2D3A30]">{reason}</p>}
        </div>
      </div>
      <button className="btn-secondary mt-4 w-full" data-testid="guide-discover-button"
        onClick={() => (config.guide_url ? window.open(config.guide_url, "_blank") : toast.info("La guida sarà disponibile a breve."))}>
        Scopri la guida
      </button>
    </Card>
  );
}

function ResultActions({ a, config, reload }) {
  const [saveOpen, setSaveOpen] = useState(false);
  return (
    <div className="space-y-4 px-5 pb-6">
      <Card testid="consultation-cta" className="!bg-[#0D2517] !border-[#C9A96E]/40 text-[#F7F4EE]">
        <p className="eyebrow !text-[#C9A96E]">Hai bisogno di una valutazione professionale?</p>
        <p className="font-serif text-xl">Richiedi una consulenza alla Dott.ssa Luigia De Vanna – Dottore Agronomo</p>
        <p className="mt-2 text-xs text-[#F7F4EE]/75">Foto, anamnesi e analisi preliminare verranno trasferite solo con il tuo consenso: non dovrai ripetere nulla.</p>
        <Link to={`/consulenza/${a.id}`} className="btn-gold mt-4 w-full" data-testid="request-consultation-button"><UserCheck size={16} /> RICHIEDI CONSULENZA</Link>
      </Card>
      {!a.plant_id ? (
        <button onClick={() => setSaveOpen(true)} className="btn-primary w-full" data-testid="save-to-plants-button"><Leaf size={16} /> SALVA IN LE MIE PIANTE</button>
      ) : (
        <Link to={`/piante/${a.plant_id}`} className="btn-secondary w-full" data-testid="go-to-plant-button"><Leaf size={16} /> Vai alla scheda della pianta</Link>
      )}
      {a.result.guide_relevant && isLemon(a.species) && config && <GuideCta config={config} reason={a.result.guide_reason} />}
      <Disclaimer compact />
      <SavePlantDialog analysis={a} open={saveOpen} onOpenChange={setSaveOpen} onSaved={reload} />
    </div>
  );
}

function ProceedCitrus({ a, reload }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    await api.post(`/analyses/${a.id}/proceed-citrus`).catch((e) => toast.error(errMsg(e)));
    setBusy(false);
    reload();
  };
  return (
    <button onClick={go} disabled={busy} className="btn-secondary min-h-[48px] w-full bg-white !text-base" data-testid="proceed-citrus-button">
      È un agrume: mostra comunque l'analisi
    </button>
  );
}

function Unsupported({ a, reload }) {
  return (
    <div className="space-y-4 px-5 py-8" data-testid="unsupported-step">
      <p className="eyebrow">Categoria non ancora disponibile</p>
      <h1 className="font-serif text-3xl font-semibold text-[#0D2517]">{a.species?.common_name} <span className="botanical text-[#B39257]">{a.species?.botanical_name}</span></h1>
      <p className="text-sm text-[#2D3A30]">La pianta è stata identificata, ma la diagnostica approfondita per questa categoria non è ancora disponibile. In questa versione l'analisi guidata è dedicata al <strong>limone e agli altri agrumi</strong>.</p>
      <p className="text-sm text-[#2D3A30]">Puoi comunque richiedere una consulenza professionale.</p>
      {a.flow === "v3" && a.v3?.pending_result && <ProceedCitrus a={a} reload={reload} />}
      <Link to={`/consulenza/${a.id}`} className="btn-primary w-full" data-testid="unsupported-consultation-button"><UserCheck size={16} /> RICHIEDI CONSULENZA</Link>
    </div>
  );
}

export default function Analysis() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [config, setConfig] = useState(null);

  const [failures, setFailures] = useState(0);
  const load = useCallback(() => api.get(`/analyses/${id}`)
    .then(({ data }) => { setA(data); setFailures(0); })
    .catch((e) => {
      // Errori temporanei dell'infrastruttura (riavvii/spostamenti del server): si riprova in silenzio
      const st = e?.response?.status;
      if (!e?.response || [502, 503, 504].includes(st) || (st >= 520 && st <= 530)) setFailures((n) => n + 1);
      else toast.error(errMsg(e));
    }), [id]);

  useEffect(() => {
    load();
    api.get("/config").then(({ data }) => setConfig(data));
  }, [load]);

  const running = a?.job?.state === "running";
  const waitingPn = a?.stage === "result" && pnPending(a?.result);
  const consultRunning = a?.stage === "result" && a?.consult?.state === "running";
  useEffect(() => {
    if (!running && !waitingPn && !consultRunning) return;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [running, waitingPn, consultRunning, load]);

  useEffect(() => {
    if (a || !failures) return;
    const t = setTimeout(load, 2500);
    return () => clearTimeout(t);
  }, [a, failures, load]);

  const unstable = failures >= 12 && (
    <p className="mx-5 mt-4 rounded-xl border border-[#c9a96e] bg-white p-3 text-xs text-[#0d2517]" data-testid="connection-unstable">
      Connessione momentaneamente instabile: l'analisi prosegue e la pagina si aggiornerà da sola appena possibile.
    </p>
  );
  if (!a) return failures ? <>{unstable}<Loader text="Connessione in corso…" /></> : <Loader />;
  if (running) return <>{unstable}<Working step={a.job.step} /></>;

  const retry = async () => {
    await api.post(`/analyses/${a.id}/retry`).catch((e) => toast.error(errMsg(e)));
    load();
  };
  const retryConsult = async () => {
    await api.post(`/analyses/${a.id}/consult`).catch((e) => toast.error(errMsg(e)));
    load();
  };

  return (
    <div data-testid="analysis-page" data-stage={a.stage}>
      {a.job?.state === "error" && (
        <div className="mx-5 mt-5 rounded-xl border border-[#B91C1C]/30 bg-[#B91C1C]/5 p-4" data-testid="analysis-error">
          <p className="text-sm text-[#7f1d1d]" data-testid="analysis-error-message">{a.job.error}</p>
          {!a.job.quota_exhausted && <button onClick={retry} className="btn-secondary mt-3 !py-2 text-xs" data-testid="analysis-retry-button"><RotateCcw size={14} /> Riprova</button>}
        </div>
      )}
      {a.stage === "photos" && <PhotoStep analysis={a} reload={load} />}
      {a.stage === "identification" && <IdentificationStep analysis={a} reload={load} />}
      {a.stage === "unsupported" && <Unsupported a={a} reload={load} />}
      {a.stage === "context" && <ContextStep analysis={a} reload={load} />}
      {a.stage === "anamnesis" && <AnamnesisStep key={a.rounds.length} analysis={a} reload={load} />}
      {a.stage === "diagnosing" && a.job?.state !== "error" && <Working step="diagnose" />}
      {a.stage === "result" && a.result && (
        <>
          <div className="space-y-4 px-5 py-6">{a.result.plan_version === 3 ? (
            <>
              {a.flow === "v3" && <SpeciesConfirm a={a} reload={load} />}
              <ResultSummary result={a.result} species={a.species} consult={a.consult} onRetryConsult={retryConsult}
                reassessSlot={a.reassess?.state === "offered" && a.job?.state !== "error" ? <ReassessPanel a={a} reload={load} /> : null} />
            </>
          ) : <ResultCard result={a.result} species={a.species} />}</div>
          <ResultActions a={a} config={config} reload={load} />
          <div className="space-y-3 px-5 pb-8 text-center" data-testid="result-share-section">
            <p className="text-sm text-[#2D3A30]">Ti è stato utile Medico delle Piante? Condividilo con chi ama le piante.</p>
            <ShareButton testid="result-share-button" />
          </div>
        </>
      )}
    </div>
  );
}
